import { useState, useCallback, useRef, useEffect } from 'react';
import type { ContentBlock } from '@shared/conversation-schema';
import type { ClientStructuredRecommendationAction } from '@shared/conversation-types';
import type { SnapshotAction } from '@shared/v2-schemas';
import { CLIENT_TURN_STALL_MS } from '@shared/conversation-timeouts';

const SSE_FETCH_MAX_ATTEMPTS = 3;
const SSE_FETCH_BASE_DELAY_MS = 250;

/** Retry transient failures (network + 5xx) for v2 SSE-opening POSTs (PL-260502 §5.1). */
async function fetchConversationSseWithBackoff(
  fetchFn: () => Promise<Response>,
): Promise<Response> {
  let lastError: unknown;
  for (let attempt = 0; attempt < SSE_FETCH_MAX_ATTEMPTS; attempt++) {
    try {
      const res = await fetchFn();
      if (res.ok) return res;
      if (res.status >= 400 && res.status < 500) return res;
      lastError = new Error(`Conversation transport failed: ${res.status}`);
    } catch (err) {
      // An aborted request is a deliberate cancellation (supersession, unmount,
      // navigation) — not a transient failure. Re-throw immediately so the
      // caller's AbortError handling runs; retrying would fire a useless extra
      // fetch (and, in tests, lets a backoff timer outlive the request and land
      // on a later test's fetch mock).
      if ((err as { name?: unknown } | null)?.name === 'AbortError') throw err;
      lastError = err;
    }
    if (attempt < SSE_FETCH_MAX_ATTEMPTS - 1) {
      await new Promise((r) => setTimeout(r, SSE_FETCH_BASE_DELAY_MS * 2 ** attempt));
    }
  }
  if (lastError instanceof Error) {
    throw lastError;
  }
  if (typeof DOMException !== 'undefined' && lastError instanceof DOMException) {
    throw lastError;
  }
  throw new Error(lastError !== undefined && lastError !== null ? String(lastError) : 'Unknown fetch failure');
}

export interface TextConversationOptions {
  projectId: string;
  mode: string;
  /**
   * Client-owned id from lifecycle (α4). When non-null, `startSession` sends
   * `is_reconnect: true` + `previous_conversation_id` per API contract. When
   * this value changes, in-flight SSE is aborted and resolved id is re-seeded.
   * Server-minted ids from SSE still update the active id used for `sendMessage`.
   */
  conversationId?: string | null;
  /**
   * Parent-composed surface boundary (lifecycle id + project + route). When it
   * changes, in-flight SSE is aborted even if `conversationId` is unchanged.
   */
  transportSurfaceKey?: string;
  /**
   * Shared lazy-start id minted by `useConversation`. When the conversation has
   * no id yet (home "type-and-go"), `sendMessage` seeds its client-minted
   * `conversation_id` from this value instead of generating an independent
   * UUID. This keeps text and voice on a single `conversation_id`: voice
   * lazy-start already keys its LiveKit room on the same shared id, so a
   * voice-first session that later sends text converges on one room/id rather
   * than diverging. Ignored once a server- or lifecycle-minted id exists.
   */
  lazyConversationId?: string;
  activityId?: string;
  participantId?: string;
  /**
   * Client pathname for cloud webhook `user.current_page`. Omitted from JSON
   * when undefined or empty.
   */
  currentPage?: string;
  /**
   * α6 — trailing turn id from parent transcript; sent as `last_seen_turn_id`
   * on reconnect `/start` only (with `is_reconnect`), so the server can slice
   * transcript replay.
   */
  lastSeenTurnId?: string;
  onContentBlock?: (block: ContentBlock) => void;
  /** Called when the gateway emits a `turn_actions` SSE event with contextual next-action pills. */
  onTurnActions?: (actions: SnapshotAction[]) => void;
  /** α7 — server idle TTL surfaced as SSE `cloud_idle_ended`. */
  onCloudIdleEnded?: (data: { conversation_id: string }) => void;
  /**
   * 038 (T025/FR-009) — a ≤2-min scoping reconnect ("we got cut off"). The
   * terminal `done` event carries `reconnect:'cut_off'`; the transcript hydrates
   * silently and the parent shows a brief "we got cut off — picking up where we
   * left off" ack. A >2-min gap instead runs the declinable welcome-back recap.
   */
  onReconnectNotice?: (data: { conversation_id: string; reconnect: 'cut_off' }) => void;
  /**
   * 011 F3 — hardened-guardrail termination. Fired when the terminal `done`
   * event carries `end_session` + `end_reason:'guardrail'` (prompt-injection /
   * out-of-scope). The streamed message has already been delivered; the parent
   * ends the conversation and surfaces a "Start again" affordance.
   */
  onCloudGuardrailEnded?: (data: { conversation_id: string }) => void;
  onError?: (error: Error) => void;
  /**
   * T020 — called when the gateway emits a `navigate` SSE event (LLM-driven
   * cross-page navigation). The payload matches the server `NavigateActionSchema`.
   */
  onNavigate?: (event: {
    target: string;
    project_short_id?: string;
    tab?: string;
    conversation_id?: string;
  }) => void;
  /**
   * `send_status_update` mid-turn progress (model-authored `text` bridge
   * sentence + a `label` chip) — the server already emits this correctly
   * (`emitMidTurnStatusUpdate`); this was the missing client-side parsing
   * branch. The parent shows `text` as an ephemeral status line, naturally
   * superseded by the next `onContentBlock` call.
   */
  onStatusUpdate?: (data: { text: string; label: string }) => void;
}

export interface SendMessageOptions {
  /**
   * Accumulated wall-time (ms) the user spent paused since the previous
   * message. Forwarded to the server as `elapsed_pause_ms` when > 0.
   * Sourced from `useVoiceConversation.consumeElapsedPause()`.
   */
  elapsedPauseMs?: number;
  /** PL-260502 §4.4 — adjunct to `transcript` on the wire (`client_structured`). */
  clientStructured?: ClientStructuredRecommendationAction;
}

export interface StartSessionOptions {
  /**
   * Mark the start as a reconnect to a previously-existing conversation
   * (browser refresh / reload). Server may honor by returning the same
   * conversation_id and replaying recent state, or fall back to a fresh id.
   */
  isReconnect?: boolean;
  /** Conversation id we believe we were attached to before disconnect. */
  previousConversationId?: string;
  /**
   * v1.0.4 — voice opening de-dupe. Set when this start exists only to resolve
   * the server conversation_id ahead of a voice connect; forwarded as
   * `defer_opening` so the gateway skips the opening turn and lets the LiveKit
   * agent's `session.start` produce the single spoken greeting.
   */
  deferOpening?: boolean;
}

export interface TextConversationReturn {
  conversationId: string | null;
  /** Turn in flight, no content from Anaka yet — the "thinking" phase. */
  isThinking: boolean;
  /**
   * Turn in flight and at least one content block has arrived — the
   * "responding" phase. Mutually exclusive with `isThinking`: a turn moves
   * thinking → responding on its first content block, and both clear when the
   * stream completes.
   */
  isResponding?: boolean;
  startSession: (options?: StartSessionOptions) => Promise<void>;
  sendMessage: (text: string, sendOpts?: SendMessageOptions) => Promise<void>;
  /**
   * Re-issue whichever request last failed. If the most recent attempt was
   * sendMessage, replay it with the same transcript + options (preserving
   * conversation_id continuity). If it was startSession, re-attempt with the
   * same reconnect args. No-op when nothing has been attempted yet.
   */
  retry: () => Promise<void>;
  error: Error | null;
}

type LastAttempt =
  | { kind: 'start'; args?: StartSessionOptions }
  | { kind: 'message'; text: string; opts?: SendMessageOptions };

export function useTextConversation(options: TextConversationOptions): TextConversationReturn {
  const {
    projectId,
    mode,
    conversationId: externalConversationId = null,
    transportSurfaceKey = '',
    lazyConversationId,
    activityId,
    participantId,
    currentPage,
    lastSeenTurnId,
    onContentBlock,
    onTurnActions,
    onCloudIdleEnded,
    onCloudGuardrailEnded,
    onReconnectNotice,
    onError,
    onNavigate,
    onStatusUpdate,
  } = options;
  const onContentBlockRef = useRef(onContentBlock);
  onContentBlockRef.current = onContentBlock;
  const onTurnActionsRef = useRef(onTurnActions);
  onTurnActionsRef.current = onTurnActions;
  const onCloudIdleEndedRef = useRef(onCloudIdleEnded);
  onCloudIdleEndedRef.current = onCloudIdleEnded;
  const onCloudGuardrailEndedRef = useRef(onCloudGuardrailEnded);
  onCloudGuardrailEndedRef.current = onCloudGuardrailEnded;
  const onReconnectNoticeRef = useRef(onReconnectNotice);
  onReconnectNoticeRef.current = onReconnectNotice;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onNavigateRef = useRef(onNavigate);
  onNavigateRef.current = onNavigate;
  const onStatusUpdateRef = useRef(onStatusUpdate);
  onStatusUpdateRef.current = onStatusUpdate;

  const [conversationId, setConversationId] = useState<string | null>(externalConversationId);
  // Two-phase in-turn status: `isThinking` while the turn is processing with no
  // content yet, `isResponding` once Anaka's first content block arrives. They
  // are mutually exclusive and both clear when the stream completes.
  const [isThinking, setIsThinking] = useState(false);
  const [isResponding, setIsResponding] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const lastAttemptRef = useRef<LastAttempt | null>(null);
  // M5 — monotonically increasing token identifying the latest request. Each
  // start/send claims a token before aborting the prior request; the `finally`
  // only clears the in-turn status when its token is still current. Without
  // this, a rapidly-superseded (aborted) request's `finally` would reset
  // `isThinking`/`isResponding` AFTER the newer request had set them, desyncing
  // the loading indicator.
  const requestSeqRef = useRef(0);

  const externalConversationIdRef = useRef(externalConversationId);
  externalConversationIdRef.current = externalConversationId;

  // Tracks the values AS OF the last time the abort/reset effect ran — not the
  // current render value. Used to distinguish first-mint (null → UUID) from a
  // true rotation (UUID-A → UUID-B): first-mint must never abort the in-flight
  // POST that triggered the lifecycle id to be minted.
  const prevExternalIdRef = useRef<string | null | undefined>(externalConversationId);
  const prevTransportSurfaceKeyRef = useRef(transportSurfaceKey);

  useEffect(() => {
    return () => { abortRef.current?.abort(); };
  }, []);

  useEffect(() => {
    const prev = prevExternalIdRef.current;
    const prevSurface = prevTransportSurfaceKeyRef.current;
    prevExternalIdRef.current = externalConversationId;
    prevTransportSurfaceKeyRef.current = transportSurfaceKey;

    // Always abort on a surface-key change (navigation / explicit reset).
    // Abort on an id ROTATION (non-null → different non-null) so that a stale
    // in-flight request from the previous conversation is cancelled.
    // Do NOT abort on a first mint (null → UUID): lifecycleSendMessage() mints
    // the id synchronously just before conversationSendMessage() posts the
    // request, so aborting here would cancel the very message being sent.
    const surfaceChanged = prevSurface !== transportSurfaceKey;
    const isRotation =
      prev != null && externalConversationId != null && prev !== externalConversationId;
    if (surfaceChanged || isRotation) {
      abortRef.current?.abort();
      abortRef.current = null;
    }
    setConversationId(externalConversationId);
    setError(null);
  }, [externalConversationId, transportSurfaceKey]);

  const parseSSEStream = useCallback(async (response: Response, options?: { requireSession?: boolean }): Promise<{ sawContent: boolean; sawActionable: boolean }> => {
    const reader = response.body?.getReader();
    if (!reader) return { sawContent: false, sawActionable: false };

    const decoder = new TextDecoder();
    let buffer = '';
    let streamError: Error | null = null;
    let sawSession = false;
    // Empty-response detection (ANA-194). `sawContent` = any renderable block
    // arrived (content/widget/layout). `sawActionable` = the turn legitimately
    // produced something non-text the user should see/act on (cold-entry pills,
    // an LLM navigate, an idle end, or an intentional session end) so a silent
    // no-op isn't mistaken for those. Returned to the caller, which flags a
    // message stream that closed with neither as a silent failure.
    let sawContent = false;
    let sawActionable = false;

    // M4 — guard each read with an idle timeout. If the server holds the
    // connection open without sending anything for this long, treat it as a
    // stalled stream and surface an error rather than leaving the UI stuck in
    // the thinking phase forever. The window is comfortably above the gateway's
    // ~10s per-step n8n timeout so a normal multi-step turn never trips it.
    const IDLE_TIMEOUT_MS = CLIENT_TURN_STALL_MS;
    // Sentinel resolved (not rejected) by the idle timer, so the timeout side of
    // the race never produces a free-floating rejected promise if the read wins.
    const STALL = Symbol('stall');

    while (true) {
      let idleTimer: ReturnType<typeof setTimeout> | undefined;
      const idle = new Promise<typeof STALL>((resolve) => {
        idleTimer = setTimeout(() => resolve(STALL), IDLE_TIMEOUT_MS);
      });
      const readResult = await Promise.race([reader.read(), idle]);
      if (idleTimer) clearTimeout(idleTimer);

      if (readResult === STALL) {
        // Release the underlying connection, then surface the stall to the
        // caller so the UI leaves the thinking phase instead of hanging.
        await reader.cancel().catch(() => {});
        throw new Error('Conversation stream stalled (no data received)');
      }

      const { done, value } = readResult;
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      let eventType = '';
      for (const line of lines) {
        if (line.startsWith('event: ')) {
          eventType = line.slice(7).trim();
        } else if (line.startsWith('data: ') && eventType) {
          try {
            const data = JSON.parse(line.slice(6));
            if (eventType === 'content' || eventType === 'widget' || eventType === 'layout') {
              // First content block of the turn → flip thinking → responding.
              // Idempotent: later blocks just keep us in the responding phase.
              setIsThinking(false);
              setIsResponding(true);
              sawContent = true;
              onContentBlockRef.current?.(data as ContentBlock);
            } else if (eventType === 'session') {
              if (data.conversation_id) {
                const id = data.conversation_id as string;
                // 032 Phase D (US1): the server owns the id. When it substitutes a
                // supplied id it now says so EXPLICITLY via `cause`
                // (foreign_owned/expired/healed_wedge/no_id_start), so we adopt the
                // resolved id directly — this replaces the old silent "id differs
                // from lifecycle id" console.warn heuristic. Additive: a `cause`
                // may be absent (flag off / no substitution); adoption is unchanged.
                setConversationId(id);
                sawSession = true;
              }
            } else if (eventType === 'done') {
              if (data.conversation_id) {
                const id = data.conversation_id as string;
                // 032 Phase D (US1): adopt the resolved id explicitly (see the
                // `session` branch above) — the id-differs console.warn is removed.
                setConversationId(id);
                sawSession = true;
                // 011 F3 — hardened-guardrail termination. The refusal message
                // has already streamed as content; tell the parent to end the
                // conversation so it can show "Start again" + route home.
                if ((data as { end_session?: unknown }).end_session === true) {
                  // An intentional session end is a legitimate terminal turn,
                  // not a silent no-op — don't flag it as an empty response.
                  sawActionable = true;
                  if ((data as { end_reason?: unknown }).end_reason === 'guardrail') {
                    onCloudGuardrailEndedRef.current?.({ conversation_id: id });
                  }
                }
                // 038 (T025/FR-009) — a ≤2-min scoping reconnect carries
                // `reconnect:'cut_off'` on the silent-hydrate `done`. Surface the
                // brief "we got cut off" ack; the transcript hydrates as usual.
                if ((data as { reconnect?: unknown }).reconnect === 'cut_off') {
                  onReconnectNoticeRef.current?.({ conversation_id: id, reconnect: 'cut_off' });
                }
              }
            } else if (eventType === 'turn_actions') {
              const actions = (data as { actions?: unknown }).actions;
              if (Array.isArray(actions)) {
                // Cold-entry pills (spec 014 US1): Anaka stays silent and emits
                // only action pills. A legitimate non-empty turn — don't flag.
                sawActionable = true;
                onTurnActionsRef.current?.(actions as SnapshotAction[]);
              }
            } else if (eventType === 'cloud_idle_ended') {
              const id =
                typeof (data as { conversation_id?: unknown }).conversation_id === 'string'
                  ? (data as { conversation_id: string }).conversation_id
                  : undefined;
              if (id) {
                sawActionable = true;
                onCloudIdleEndedRef.current?.({ conversation_id: id });
              }
            } else if (eventType === 'navigate') {
              // T020 — LLM-driven cross-page navigation. The gateway emits this
              // before `done` when the mode-response contains a `navigate` action.
              const navData = data as {
                target?: string;
                project_short_id?: string;
                tab?: string;
                conversation_id?: string;
              };
              if (typeof navData.target === 'string') {
                // An LLM-driven navigation is a real outcome of the turn even
                // when no text streamed — don't flag it as an empty response.
                sawActionable = true;
                onNavigateRef.current?.(navData as {
                  target: string;
                  project_short_id?: string;
                  tab?: string;
                  conversation_id?: string;
                });
              }
            } else if (eventType === 'status_update') {
              // send_status_update mid-turn progress — never gates sawContent/
              // sawActionable (it's chrome, not turn output); a genuinely empty
              // turn that only ever sent status updates should still be flagged.
              const text = (data as { text?: unknown }).text;
              const label = (data as { label?: unknown }).label;
              if (
                typeof text === 'string' &&
                text.length > 0 &&
                typeof label === 'string' &&
                label.length > 0
              ) {
                onStatusUpdateRef.current?.({ text, label });
              }
            } else if (eventType === 'heartbeat') {
              // spec 032 T024a(b) — a liveness-gated keepalive from the gateway
              // while a long-but-alive n8n step runs. Intentionally renders
              // nothing: its only job is to make `reader.read()` resolve, which
              // re-arms the IDLE_TIMEOUT_MS stall watchdog on the next loop
              // iteration, so a >45s alive turn no longer false-errors. When the
              // turn genuinely dies the gateway stops heartbeating, the read goes
              // silent, and the stall timer fires as before.
            } else if (eventType === 'error') {
              // Server emitted an error event mid-stream — surface it instead
              // of leaving the conversation silently stuck in the connecting
              // phase. Common shape: { message: string } from
              // `server/routes/conversation-v2.ts`, but also accept top-level
              // string / `error` fields for resilience.
              const messageFromData =
                (typeof (data as { message?: unknown }).message === 'string'
                  ? (data as { message: string }).message
                  : typeof (data as { error?: unknown }).error === 'string'
                    ? (data as { error: string }).error
                    : typeof data === 'string'
                      ? data
                      : null) ?? 'Conversation failed to start';
              streamError = new Error(messageFromData);
            }
          } catch (parseErr) {
            // Don't crash the whole stream on one bad frame, but surface it —
            // silent skipping makes a dropped content block indistinguishable
            // from "chat froze for no reason" when debugging.
            console.warn('[useTextConversation] malformed SSE frame skipped', {
              event: eventType,
              error: parseErr instanceof Error ? parseErr.message : String(parseErr),
            });
          }
          eventType = '';
        }
      }
    }

    if (streamError) {
      throw streamError;
    }
    // Caller (startSession) requires a session event so the conversation
    // phase can transition to `awaiting_first_turn` and the input bar can
    // enable. If the SSE stream ends without one and without an explicit
    // `event: error`, treat it as a silent server failure — surfacing the
    // problem to the user beats leaving the input disabled forever.
    if (options?.requireSession && !sawSession) {
      throw new Error(
        'Conversation did not start: server closed the stream without a session event.',
      );
    }
    return { sawContent, sawActionable };
  }, []);

  const startSession = useCallback(async (startOptions?: StartSessionOptions) => {
    lastAttemptRef.current = { kind: 'start', args: startOptions };
    const myReq = ++requestSeqRef.current;
    try {
      setIsThinking(true);
      setIsResponding(false);
      setError(null);
      // Abort any in-flight request before installing a new controller — without this,
      // a rapid second call orphans the first reader (the previous signal is still
      // referenced by the in-flight fetch) and parseSSEStream keeps reading until the
      // server closes the connection.
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;

      const isReconnect = startOptions?.isReconnect === true;
      const previousConversationId = startOptions?.previousConversationId;
      // T025 D6: only send last_seen_turn_id when it is server-originated.
      // Runtime-minted ids use `user_<ts>_<n>` / `assistant_<ts>_<n>` prefix;
      // sending those would reference a client-local id the server cannot resolve.
      const isClientMintedTurnId = (id: string) =>
        /^(assistant|user)_\d+_\d+$/.test(id);
      const includeLastSeenTurn =
        isReconnect &&
        typeof lastSeenTurnId === 'string' &&
        lastSeenTurnId.length > 0 &&
        !isClientMintedTurnId(lastSeenTurnId);

      const response = await fetchConversationSseWithBackoff(() =>
        fetch('/api/v1/v2/conversation/start', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            // spec 032 — the client owns the conversation id. Send it to /start
            // so start and message operate on ONE conversation (the server uses
            // it verbatim, owner-scoped). On RECONNECT the authoritative target
            // is `previous_conversation_id` — DB ground truth that may differ
            // from a stale sessionStorage lifecycle id — so it wins; otherwise
            // use the lifecycle id. (A stale lifecycle id must never override the
            // DB-authoritative resume.) When neither is set the server mints one
            // and the client adopts it from the `session` event.
            ...((previousConversationId ?? externalConversationIdRef.current)
              ? { conversation_id: previousConversationId ?? externalConversationIdRef.current }
              : {}),
            ...(projectId ? { project_id: projectId } : {}),
            mode: mode,
            activity_id: activityId,
            participant_id: participantId,
            ...(currentPage ? { current_page: currentPage } : {}),
            ...(isReconnect ? { is_reconnect: true } : {}),
            ...(previousConversationId
              ? { previous_conversation_id: previousConversationId }
              : {}),
            ...(includeLastSeenTurn ? { last_seen_turn_id: lastSeenTurnId } : {}),
            ...(startOptions?.deferOpening ? { defer_opening: true } : {}),
          }),
          signal: ac.signal,
        }),
      );

      if (!response.ok) throw new Error(`Session start failed: ${response.status}`);
      await parseSSEStream(response, { requireSession: true });
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        onErrorRef.current?.(error);
      }
    } finally {
      // Only clear status if this is still the current request (M5).
      if (requestSeqRef.current === myReq) {
        setIsThinking(false);
        setIsResponding(false);
      }
    }
  }, [projectId, mode, activityId, participantId, currentPage, lastSeenTurnId, parseSSEStream]);

  const sendMessage = useCallback(async (text: string, sendOpts?: SendMessageOptions) => {
    lastAttemptRef.current = { kind: 'message', text, opts: sendOpts };
    const myReq = ++requestSeqRef.current;
    // Lazy-start: when no conversation_id has been minted yet (server-side via
    // /start, or by a previous lifecycle event), mint one client-side and let
    // the server's /message handler create the mapping on the fly. This is
    // what powers the home page "type-and-go" UX — no /start round-trip
    // required before the first turn.
    let activeConvId = conversationId;
    if (!activeConvId) {
      // Prefer the shared lazy id from `useConversation` so text and voice key
      // on ONE conversation_id: voice lazy-start already rooms on this id, so a
      // voice-first session that later sends text converges instead of
      // diverging. Fall back to a freshly-minted id when no shared id was
      // supplied (standalone text-only consumers). No channel prefix: a single
      // conversation can flip between text and voice, so the id stays
      // channel-agnostic.
      const minted =
        lazyConversationId ??
        (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      activeConvId = minted;
      setConversationId(minted);
    }

    try {
      setIsThinking(true);
      setIsResponding(false);
      setError(null);
      // Abort any in-flight request before installing a new controller (see startSession).
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;

      const elapsedPauseMs = sendOpts?.elapsedPauseMs;
      const includeElapsedPause = typeof elapsedPauseMs === 'number' && elapsedPauseMs > 0;
      const clientStructured = sendOpts?.clientStructured;

      const response = await fetchConversationSseWithBackoff(() =>
        fetch('/api/v1/v2/conversation/message', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            conversation_id: activeConvId,
            ...(projectId ? { project_id: projectId } : {}),
            transcript: text,
            mode: mode,
            input_type: 'text',
            ...(currentPage ? { current_page: currentPage } : {}),
            ...(includeElapsedPause ? { elapsed_pause_ms: elapsedPauseMs } : {}),
            ...(clientStructured !== undefined ? { client_structured: clientStructured } : {}),
          }),
          signal: ac.signal,
        }),
      );

      if (!response.ok) throw new Error(`Message failed: ${response.status}`);
      const { sawContent, sawActionable } = await parseSSEStream(response);
      // Empty-response detection (ANA-194). The stream closed cleanly without
      // any renderable content AND without an actionable signal (pills /
      // navigate / idle-end / intentional end) — a silent no-op: the user sent
      // something and got nothing back. Surface it so the UI shows error +
      // Retry instead of quietly dropping the turn. The 45s idle stall covers
      // the "held open" case; this covers "closed empty". Only the CURRENT
      // request is flagged — a superseded one is intentionally discarded, so a
      // stale stream closing empty must not raise a spurious error.
      if (requestSeqRef.current === myReq && !sawContent && !sawActionable) {
        throw new Error('Conversation produced no response');
      }
    } catch (err) {
      // Swallow aborts (superseded request / unmount) — they aren't failures.
      // Re-throw genuine errors so the caller (useConversation) can roll the
      // optimistic user turn back to a failed/retryable state (M1).
      if ((err as Error).name === 'AbortError') return;
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      onErrorRef.current?.(error);
      throw error;
    } finally {
      // Only clear status if this is still the current request (M5).
      if (requestSeqRef.current === myReq) {
        setIsThinking(false);
        setIsResponding(false);
      }
    }
  }, [conversationId, lazyConversationId, projectId, mode, currentPage, parseSSEStream]);

  const retry = useCallback(async () => {
    const last = lastAttemptRef.current;
    if (last == null) return;
    // sendMessage now rejects on genuine failure (M1); the error state is
    // already surfaced via setError/onError, so swallow here to avoid an
    // unhandled rejection from the retry affordance.
    try {
      if (last.kind === 'message') {
        await sendMessage(last.text, last.opts);
      } else {
        await startSession(last.args);
      }
    } catch {
      /* error already surfaced by the underlying call */
    }
  }, [sendMessage, startSession]);

  return { conversationId, isThinking, isResponding, startSession, sendMessage, retry, error };
}
